import React from 'react';
import {View, ViewStyle} from 'react-native';

import Card from '/component/base/Card';
import InlineFormattedNumber from '/component/base/FormattedNumber/InlineFormattedNumber';
import ListItem from '/component/base/ListItem';
import Text from '/component/base/Text';
import {Cards} from '/constant/TestIDs';
import {useTranslation} from '/provider/TranslationProvider';

import {styles} from './OrdersSummaryCard.style';
import {Competency, getOrderSummaryKeys} from './OrderSummaryCard.util';

const MAX_FONT_SIZE_MULTIPLIER = 2;
interface Metrics {
  currentVolume?: number;
  projectedVolume?: number;
  estimatedVolume?: number;
}
interface OrdersSummaryCardProps {
  metrics: Metrics;
  currentUserCompetency: Competency;
  style: ViewStyle;
}

export const OrdersSummaryCard: React.FC<OrdersSummaryCardProps> = ({
  currentUserCompetency,
  metrics = {},
  style = {},
}) => {
  const {
    currentKey,
    currentLabel,
    currentTestID,
    currentTitleTestID,
    projectedKey,
    projectedLabel,
    projectedTestID,
    projectedTitleTestID,
    estimatedKey,
    estimatedLabel,
    estimatedTestID,
    estimatedTitleTestID,
  } = getOrderSummaryKeys(currentUserCompetency);
  const {tt} = useTranslation('screen.clients.card.orders');

  return (
    <Card
      fullWidth
      testID={Cards.ordersSummaryCard}
      style={{...styles.base, ...style}}
      accessible={false}>
      <View
        accessible={true}
        importantForAccessibility="yes"
        accessibilityRole="header"
        accessibilityLabel={'Orders This Month'}>
        <Text
          testID={Cards.ordersSummaryCardTitle}
          variant="heading"
          style={styles.cardHeaderText}
          allowFontScaling
          maxFontSizeMultiplier={MAX_FONT_SIZE_MULTIPLIER}
          accessible={false}
          importantForAccessibility="no">
          {tt('card.clients.header', 'Orders This Month')}
        </Text>
      </View>
      <ListItem
        accessibilityLabel={`${currentLabel}, ${metrics?.currentVolume ?? 0}`}
        label={
          <Text
            testID={currentTitleTestID}
            accessible={true}
            variant="body"
            emphasis="low"
            // @ts-expect-error TS(2551): Property 'listItemLabel' does not exist on type '{... Remove this comment to see the full error message
            style={styles.listItemLabel}
            allowFontScaling
            maxFontSizeMultiplier={MAX_FONT_SIZE_MULTIPLIER}>
            {tt(`${currentKey}`, `${currentLabel}`)}
          </Text>
        }
        readonly
        suffix={
          <InlineFormattedNumber
            testID={currentTestID}
            value={`${metrics.currentVolume}`}
            truncate
          />
        }
        style={{base: styles.listItem, suffix: styles.listItemSuffix}}
      />
      <ListItem
        accessibilityLabel={`${projectedLabel}, ${
          metrics.projectedVolume ?? 0
        }`}
        label={
          <Text
            testID={projectedTitleTestID}
            accessible={true}
            variant="body"
            emphasis="low"
            // @ts-expect-error TS(2551): Property 'listItemLabel' does not exist on type '{... Remove this comment to see the full error message
            style={styles.listItemLabel}
            allowFontScaling
            maxFontSizeMultiplier={MAX_FONT_SIZE_MULTIPLIER}>
            {tt(`${projectedKey}`, `${projectedLabel}`)}
          </Text>
        }
        readonly
        suffix={
          <InlineFormattedNumber
            testID={projectedTestID}
            value={`${metrics?.projectedVolume}`}
            truncate
          />
        }
        style={{base: styles.listItem, suffix: styles.listItemSuffix}}
      />
      <ListItem
        accessibilityLabel={`${estimatedLabel}, ${
          metrics.estimatedVolume ?? 0
        }`}
        label={
          <Text
            testID={estimatedTitleTestID}
            accessible={true}
            variant="body"
            emphasis="low"
            // @ts-expect-error TS(2551): Property 'listItemLabel' does not exist on type '{... Remove this comment to see the full error message
            style={styles.listItemLabel}
            allowFontScaling
            maxFontSizeMultiplier={MAX_FONT_SIZE_MULTIPLIER}>
            {tt(`${estimatedKey}`, `${estimatedLabel}`)}
          </Text>
        }
        readonly
        suffix={
          <InlineFormattedNumber
            testID={estimatedTestID}
            value={`${metrics.estimatedVolume}`}
            truncate
          />
        }
        style={{
          base: {...styles.listItem, ...styles.listItemLast},
          suffix: styles.listItemSuffix,
        }}
      />
    </Card>
  );
};

export default OrdersSummaryCard;
